import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { toast } from "sonner";
import { FileDown, Send, Plus, Check } from "lucide-react";

const STATUS_TONE = {
  draft: "bg-anvi-100 text-anvi-700",
  sent: "bg-blue-50 text-blue-700",
  viewed: "bg-purple-50 text-purple-800",
  approved: "bg-emerald-50 text-emerald-700",
};

export default function QuotationsPanel({ projectId }) {
  const [quotations, setQuotations] = useState([]);
  const [tier, setTier] = useState("Standard");
  const [busy, setBusy] = useState(false);

  const load = () => api.quotationsList(projectId).then((r) => setQuotations(r.data));
  useEffect(() => { load(); }, [projectId]);

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.quotationCreate(projectId, tier);
      toast.success(`Quotation v${r.data.version} created — ${r.data.quote_number}`);
      load();
    } catch { toast.error("Could not create quotation"); }
    finally { setBusy(false); }
  };

  const sendQuo = async (id) => {
    try { await api.quotationMarkSent(id); toast.success("Marked as sent (WhatsApp/Email MOCKED)"); load(); }
    catch { toast.error("Failed"); }
  };

  const openPdf = (id) => window.open(api.quotationPdfUrl(id), "_blank");

  return (
    <div className="space-y-6" data-testid="quotations-panel">
      <div className="bg-white border border-anvi-200 rounded-md p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Generate quotation</div>
            <div className="font-heading text-xl text-anvi-900 mt-1">Branded PDF · always in sync with BOQ</div>
            <div className="text-xs text-anvi-500 mt-1">Editing the BOQ or Estimate creates a new version — never mutates the existing PDF.</div>
          </div>
          <div className="flex items-center gap-3">
            <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="quotation-tier-select"
                    className="border border-anvi-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-anvi-terracotta">
              {["Economy", "Standard", "Premium"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <button onClick={create} disabled={busy} data-testid="generate-quotation-btn"
                    className="flex items-center gap-2 bg-anvi-terracotta hover:bg-anvi-terracotta-dark text-white px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60">
              <Plus size={14} strokeWidth={1.5} /> {busy ? "Generating…" : "Generate PDF"}
            </button>
          </div>
        </div>
      </div>

      {quotations.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-anvi-200 rounded-md text-anvi-500 text-sm">
          No quotations yet · generate v1 above
        </div>
      ) : (
        <div className="bg-white border border-anvi-200 rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] tracking-[0.15em] uppercase text-anvi-500 border-b border-anvi-200 bg-anvi-50/50">
                <th className="px-4 py-3 text-left font-medium">Version</th>
                <th className="px-4 py-3 text-left font-medium">Quote #</th>
                <th className="px-4 py-3 text-left font-medium">Tier</th>
                <th className="px-4 py-3 text-right font-medium">Grand Total</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Created</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotations.map((q) => (
                <tr key={q.id} className="border-b border-anvi-100 hover:bg-anvi-50/60 transition-colors" data-testid={`quotation-row-${q.id}`}>
                  <td className="px-4 py-3.5 font-heading text-lg text-anvi-900">v{q.version}</td>
                  <td className="px-4 py-3.5 font-mono text-xs text-anvi-700">{q.quote_number}</td>
                  <td className="px-4 py-3.5 text-sm text-anvi-900">{q.tier}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-medium tabular-nums text-anvi-900">{inr(q.grand_total)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_TONE[q.status]}`}>{q.status}</span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-anvi-500 tabular-nums">{new Date(q.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => openPdf(q.id)} data-testid={`open-pdf-${q.id}`}
                              className="text-anvi-terracotta hover:bg-anvi-terracotta/10 p-1.5 rounded-md" title="Download PDF">
                        <FileDown size={14} strokeWidth={1.5} />
                      </button>
                      {q.status === "draft" && (
                        <button onClick={() => sendQuo(q.id)} data-testid={`send-quotation-${q.id}`}
                                className="text-xs bg-anvi-900 hover:bg-anvi-800 text-white px-2.5 py-1 rounded-md flex items-center gap-1">
                          <Send size={11} strokeWidth={1.5} /> Send
                        </button>
                      )}
                      {q.status !== "draft" && (
                        <span className="text-xs text-emerald-700 flex items-center gap-1"><Check size={11} strokeWidth={2} /> sent</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
